import { RuntimeError } from '@multiplier/lib-shared-errors';
import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { type FeatureFlags, useFeatureFlagsQuery } from '@/hooks/queries/use-feature-flags';

interface FeatureFlagContextType {
  flags: FeatureFlags | undefined;
  isLoading: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const { data, isLoading } = useFeatureFlagsQuery();

  const value = useMemo(
    () => ({
      flags: data?.flags,
      isLoading,
    }),
    [data, isLoading]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new RuntimeError('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
};

export const useFeatureFlag = (flagName: keyof FeatureFlags): boolean => {
  const { flags } = useFeatureFlags();
  return flags?.[flagName] === true;
};
